import { devSimulatePing } from '~/rage/decorators';
import { Subject } from '~/rage/utils';
import { ClientError, ClientErrorCode, RewardRaw, RewardType } from '~/shared';
import {
	IPromoCode,
	IPromoCodeAdmin,
	IPromoService,
} from './promoServiceTypes';

export class PromoServiceStub implements IPromoService {
	private codes: IPromoCode[] = [
		{
			code: 'STUBCODE1',
			playTimePassedMin: 0,
			playTimeRequiredMin: 5,
			activatedAt: 0,
			isClaimed: false,
			rewards: [
				{
					type: RewardType.MONEY,
					value: '1000',
				},
				{
					type: RewardType.EXP,
					value: '100',
				},
				{
					type: RewardType.VEHICLE,
					value: JSON.stringify({ model: 'gaz_2410' }),
				},
			],
		},
		{
			code: 'STUBCODE2',
			playTimePassedMin: 0,
			playTimeRequiredMin: 30,
			activatedAt: 0,
			isClaimed: false,
			rewards: [
				{
					type: RewardType.MONEY,
					value: '1000',
				},
				{
					type: RewardType.EXP,
					value: '100',
				},
			],
		},
		{
			code: 'STUBCODE3',
			playTimePassedMin: 0,
			playTimeRequiredMin: 120,
			activatedAt: 0,
			isClaimed: false,
			rewards: [
				{
					type: RewardType.VEHICLE,
					value: JSON.stringify({ model: 'c63' }),
				},
			],
		},
		{
			code: 'STUBCODE_CASE',
			playTimePassedMin: 0,
			playTimeRequiredMin: 5,
			activatedAt: 0,
			isClaimed: false,
			rewards: [
				{
					type: RewardType.LOOTBOX,
					value: 'bronze_case',
				},
			],
		},
		{
			code: 'STUBCODE_BUNDLE',
			playTimePassedMin: 0,
			playTimeRequiredMin: 5,
			activatedAt: 0,
			isClaimed: false,
			rewards: [
				{
					type: RewardType.BUNDLE,
					value: 'kit_test',
				},
			],
		},
		{
			code: 'STUBCODE_MIX',
			playTimePassedMin: 0,
			playTimeRequiredMin: 10,
			activatedAt: 0,
			isClaimed: false,
			rewards: [
				{
					type: RewardType.MONEY,
					value: '50000',
				},
				{
					type: RewardType.LOOTBOX,
					value: 'bronze_case',
				},
				{
					type: RewardType.BUNDLE,
					value: 'kit_test',
				},
				{
					type: RewardType.ITEM,
					value: JSON.stringify({ id: 'baconizer', count: 3 }),
				},
			],
		},
	];

	// Admin promo codes (created via createPromoCode)
	private adminPromoCodes: IPromoCodeAdmin[] = [
		{
			code: 'STUB_ADMIN_1',
			enabledTimestampStart: Date.now() - 86400000,
			enabledTimestampEnd: Date.now() + 86400000,
			requiredPlayTimeMin: 0,
			maxActivations: null,
			activationCount: 0,
			rewards: [
				{
					type: RewardType.MONEY,
					value: '50000',
				},
			],
		},
	];

	private readonly promoCodes = new Subject<IPromoCode[]>([]);

	constructor() {
		this.fetchPromoCodes();
	}

	@devSimulatePing()
	public async fetchPromoCodes(): Promise<void> {
		this.promoCodes.next(this.codes);
	}

	public getPromoCodes() {
		return this.promoCodes;
	}

	@devSimulatePing()
	public async activateCode(code: string): Promise<void> {
		const promoCode = this.codes.find((c) => c.code === code);

		if (!promoCode) {
			throw new ClientError(ClientErrorCode.PromoCodeNotFound);
		}

		this.codes.push({
			code: `NEW_${code}`,
			playTimePassedMin: 0,
			playTimeRequiredMin: 10,
			activatedAt: 0,
			isClaimed: false,
			rewards: [
				{
					type: RewardType.MONEY,
					value: '999999',
				},
				{
					type: RewardType.EXP,
					value: '222',
				},
				{
					type: RewardType.VEHICLE,
					value: JSON.stringify({ model: 'gaz_2410' }),
				},
			],
		});
		this.fetchPromoCodes();
	}

	@devSimulatePing()
	public async claimRewards(code: string): Promise<void> {
		const promoCode = this.codes.find((c) => c.code === code);

		if (!promoCode) {
			throw new ClientError(ClientErrorCode.PromoCodeNotFound);
		}

		promoCode.isClaimed = true;

		this.fetchPromoCodes();
	}

	@devSimulatePing()
	public async createPromoCode(payload: {
		code: string;
		dateStart: number | null;
		dateEnd: number | null;
		requiredPlayTimeMin: number;
		maxActivations: number | null;
		rewards: { type: string; value: string }[];
	}): Promise<void> {
		console.log('createPromoCode called with payload:', payload);

		const newPromo: IPromoCodeAdmin = {
			code: payload.code,
			enabledTimestampStart: payload.dateStart,
			enabledTimestampEnd: payload.dateEnd,
			requiredPlayTimeMin: payload.requiredPlayTimeMin,
			maxActivations: payload.maxActivations,
			activationCount: 0,
			rewards: payload.rewards.map((r) => ({
				type: r.type as RewardRaw['type'],
				value: r.value,
			})),
		};

		this.adminPromoCodes.push(newPromo);
		console.log('Stored promo in stub service:', this.adminPromoCodes);
	}

	public async fetchAllPromoCodes(): Promise<IPromoCodeAdmin[]> {
		return [...this.adminPromoCodes];
	}

	public async deletePromoCode(code: string): Promise<boolean> {
		const index = this.adminPromoCodes.findIndex((p) => p.code === code);
		if (index !== -1) {
			this.adminPromoCodes.splice(index, 1);
			return true;
		}
		return false;
	}

	public async exitMenu(): Promise<void> {
		console.log('closePromo called (stub)');
	}
}
